import { Controller } from '@hotwired/stimulus'
import { renderStreamMessage } from '@hotwired/turbo'
import { computePosition, flip, shift, offset } from '@floating-ui/dom'

const TASK_ID_PLACEHOLDER = '__TASK_ID__'

export default class extends Controller {
  static targets = ['menu', 'statusOptions', 'statusFrame']

  connect() {
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.closeOnScroll = this.closeOnScroll.bind(this)
  }

  disconnect() {
    this.close()
  }

  open(event) {
    event.preventDefault()
    event.stopPropagation()

    const { menu, taskId, taskType, statusesUrl } = event.params
    const dialog = this.menuTargets.find((target) => target.dataset.menu === menu)
    const reopeningSame = this.openMenu === dialog && this.taskId === String(taskId)
    this.close()
    if (!dialog || reopeningSame) return

    this.taskId = String(taskId)
    this.trigger = event.currentTarget
    if (menu === 'status') this.fillStatusOptions(taskType, statusesUrl)

    this.openMenu = dialog
    dialog.show()
    dialog.classList.add('show')
    this.position()

    document.addEventListener('click', this.closeOnOutsideClick)
    document.addEventListener('keydown', this.closeOnEscape)
    window.addEventListener('scroll', this.closeOnScroll, true)
  }

  select(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url.replace(TASK_ID_PLACEHOLDER, this.taskId)
    this.close()

    fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        Accept: 'text/vnd.turbo-stream.html'
      }
    })
      .then((response) => response.text())
      .then((html) => renderStreamMessage(html))
  }

  closeAfterChoice(event) {
    if (event.target.closest('.dropdown-item')) setTimeout(() => this.close(), 0)
  }

  close() {
    if (!this.openMenu) return

    this.openMenu.classList.remove('show')
    this.openMenu.close()
    this.openMenu = null
    this.trigger = null

    document.removeEventListener('click', this.closeOnOutsideClick)
    document.removeEventListener('keydown', this.closeOnEscape)
    window.removeEventListener('scroll', this.closeOnScroll, true)
  }

  position() {
    if (!this.openMenu || !this.trigger) return

    const menu = this.openMenu
    computePosition(this.trigger, menu, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [offset(4), flip(), shift({ padding: 8 })]
    }).then(({ x, y }) => Object.assign(menu.style, { left: `${x}px`, top: `${y}px` }))
  }

  fillStatusOptions(taskType, statusesUrl) {
    const template = this.menuTargets
      .find((target) => target.dataset.menu === 'status')
      .querySelector(`template[data-task-type="${taskType}"]`)

    if (template) {
      this.statusOptionsTarget.replaceChildren(template.content.cloneNode(true))
      this.statusFrameTarget.replaceChildren()
      this.statusFrameTarget.removeAttribute('src')
    } else {
      this.statusOptionsTarget.replaceChildren()
      this.statusFrameTarget.replaceChildren(this.spinner())
      this.statusFrameTarget.removeAttribute('src')
      this.statusFrameTarget.src = statusesUrl
    }
  }

  spinner() {
    const item = document.createElement('div')
    item.className = 'dropdown-item text-center'
    item.innerHTML = '<i class="fa-duotone fa-loader fa-spin-pulse"></i>'
    return item
  }

  closeOnOutsideClick(event) {
    if (!this.openMenu?.contains(event.target)) this.close()
  }

  closeOnEscape(event) {
    if (event.key === 'Escape') this.close()
  }

  closeOnScroll(event) {
    if (!this.openMenu?.contains(event.target)) this.close()
  }
}
